/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getApi } from '../common/requestHelper';
import {
  CreateReferenceStoreDto,
  CreateReferenceStoreResponseDtoStatusEnum,
  ReferenceStoreApi,
  ReferenceStoreDto,
} from '@ink-ai/insight-service-sdk';
import { app } from './app';
import { FileUploadStatus } from '../insight-hub/components/file-upload/FileUpload';
import { downloadFromLink } from '../common/utils';

export const statusStringMapping: Record<
  CreateReferenceStoreResponseDtoStatusEnum,
  string
> = {
  [CreateReferenceStoreResponseDtoStatusEnum.Importing]:
    'referenceStore.status.importing',
  [CreateReferenceStoreResponseDtoStatusEnum.Ready]:
    'referenceStore.status.ready',
  [CreateReferenceStoreResponseDtoStatusEnum.Error]:
    'referenceStore.status.failed',
};

export const statusClassMapping: Record<
  CreateReferenceStoreResponseDtoStatusEnum,
  string
> = {
  [CreateReferenceStoreResponseDtoStatusEnum.Importing]: 'status-info',
  [CreateReferenceStoreResponseDtoStatusEnum.Ready]: 'status-success',
  [CreateReferenceStoreResponseDtoStatusEnum.Error]: 'status-error',
};

export type ReferenceStoreState = {
  referenceStores: ReferenceStoreDto[];
  loading: boolean;
  selectedReferenceStore: ReferenceStoreDto | null;
  FileUploadStatus?: FileUploadStatus | null;
  progress: number;
};

const initialState: ReferenceStoreState = {
  referenceStores: [],
  loading: false,
  selectedReferenceStore: null,
  FileUploadStatus: FileUploadStatus.WaitingUpload,
  progress: 0,
};

export const handleDownload = async (id: string, suggestedName?: string) => {
  try {
    const referenceStoreApi = await getApi(ReferenceStoreApi);
    const response = await referenceStoreApi.getReferenceStoreDownloadLink(id);
    const name = suggestedName || `reference-${id}.csv`;
    const finalName = name.toLowerCase().endsWith('.csv') ? name : `${name}.csv`;
    await downloadFromLink(response.data.downloadLink, finalName);
  } catch (error: any) {
    console.error('Failed to download the reference store:', error);
  }
};

export const listReferenceStores = createAsyncThunk<ReferenceStoreDto[]>(
  'referenceStore/listReferenceStores',
  async () => {
    const referenceStoreApi = await getApi(ReferenceStoreApi);
    const response = await referenceStoreApi.listReferenceStores();
    // 兼容代理层返回结构：可能为 { data: { referenceStores } } 或直接 { referenceStores }
    const payload: any = (response as any)?.data ?? response;
    return (
      payload?.referenceStores ??
      payload?.data?.referenceStores ??
      []
    );
  },
);

export const getReferenceStoreById = createAsyncThunk(
  'referenceStore/getReferenceStoreById',
  async (uuid: string) => {
    const referenceStoreApi = await getApi(ReferenceStoreApi);
    const response = await referenceStoreApi.getReferenceStoreById(uuid);
    // 兼容代理层返回结构：可能为 { data: ReferenceStoreDto } 或 { data: { data: ReferenceStoreDto } }
    const data: any = (response as any)?.data;
    return data?.data ?? data;
  },
);

export const deleteReferenceStores = createAsyncThunk(
  'referenceStore/deleteReferenceStores',
  async (referenceStoreIds: string[], { getState, dispatch }) => {
    const state = getState() as { referenceStore: ReferenceStoreState };
    const referenceStoreApi = await getApi(ReferenceStoreApi);

    console.log('[ReferenceStoreThunk] deleteReferenceStores start', { count: referenceStoreIds?.length ?? 0, ids: referenceStoreIds });

    // 直接使用传入的 ID 调用删除接口，避免依赖 state 列表匹配而跳过
    const deletePromises = referenceStoreIds.map(async (uuid) => {
      try {
        console.log('[ReferenceStoreThunk] deleting reference store', { id: uuid });
        await referenceStoreApi.deleteReferenceStore(uuid);
      } catch (e: any) {
        console.error('[ReferenceStoreThunk] deleteReferenceStores failed for id', { id: uuid, error: e?.message || e });
      }
    });

    await Promise.all(deletePromises);
    console.log('[ReferenceStoreThunk] deleteReferenceStores completed');
    dispatch(listReferenceStores());
  },
);

export const importReferenceStore = createAsyncThunk(
  'referenceStore/importReferenceStore',
  async (
    {
      name,
      description,
      uuid,
      uploadTaskId,
    }: {
      name: string;
      description: string;
      uploadTaskId: string;
      uuid?: string;
    },
    { dispatch, rejectWithValue },
  ) => {
    const referenceStoreApi = await getApi(ReferenceStoreApi);
    try {
      if (uuid) {
        const newReferenceStore: CreateReferenceStoreDto = {
          name: name,
          desc: description,
          uploadTaskId,
        };
        await referenceStoreApi.updateUploadReferenceStore(
          uuid,
          newReferenceStore,
        );
      } else {
        await referenceStoreApi.createReferenceStore({
          name,
          desc: description,
          uploadTaskId,
        });
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || 'Failed to import reference store';
      // 回退前端状态，避免停留在上传/导入态
      dispatch(
        referenceStoreActions.setFileUploadStatus({
          status: FileUploadStatus.WaitingUpload,
        }),
      );
      return rejectWithValue({ status, message });
    }
    dispatch(
      referenceStoreActions.setFileUploadStatus({
        status: FileUploadStatus.Importing,
      }),
    );
    dispatch(app.actions.setNavigationPath('/reference-management'));
  },
);

export const referenceStore = createSlice({
  name: 'referenceStore',
  initialState,
  reducers: {
    setLoading(state, { payload }: PayloadAction<boolean>) {
      state.loading = payload;
      console.log('state.loading: ', state.loading);
    },
    setSelectedReferenceStore(
      state,
      { payload }: PayloadAction<ReferenceStoreDto | null>,
    ) {
      state.selectedReferenceStore = payload;
    },
    setProgress(state, action: PayloadAction<number>) {
      state.progress = action.payload;
    },
    setFileUploadStatus(
      state,
      {
        payload,
      }: PayloadAction<{
        status: FileUploadStatus | null;
      }>,
    ) {
      state.FileUploadStatus = payload.status;
    },
    clearAll: () => {
      return {
        ...initialState,
        timestamp: Date.now(),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listReferenceStores.pending, (state) => {
        state.loading = true;
      })
      .addCase(listReferenceStores.fulfilled, (state, action) => {
        state.referenceStores = action.payload;
        state.loading = false;
      })
      .addCase(listReferenceStores.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getReferenceStoreById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReferenceStoreById.fulfilled, (state, action) => {
        state.selectedReferenceStore = action.payload;
        state.loading = false;
      })
      .addCase(getReferenceStoreById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(importReferenceStore.pending, (state) => {
        state.loading = true;
      })
      .addCase(importReferenceStore.fulfilled, (state) => {
        state.loading = true; // it is still in importing state
        state.progress = 0;
      })
      .addCase(importReferenceStore.rejected, (state) => {
        state.loading = false;
        state.FileUploadStatus = FileUploadStatus.Error;
        state.progress = 0;
      });
  },
});

export const referenceStoreActions = referenceStore.actions;
export default referenceStore.reducer;
